import React, { useMemo, createContext, useContext } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useAuth, useResolvedRestaurant, useTenantAccess } from './tenant';
import { ResolvedRestaurant, UserRestaurantAccess, TenantRole } from './types';
import { Skeleton } from '../components/ui/Skeleton';
import { getSubdomainSlug } from './subdomain';
// ─── Context Shape ───────────────────────────────────────────
interface TenantContextValue {
  restaurant: ResolvedRestaurant;
  access: UserRestaurantAccess;
  role: TenantRole;
  permissions: string[];
  user: ReturnType<typeof useAuth>['user'];
  signOut: () => Promise<void>;
}
const TenantContext = createContext<TenantContextValue | null>(null);
export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return ctx;
}
// ─── Loading Screen ──────────────────────────────────────────
function LoadingScreen({
  message
}: {
  message: string;
}) {
  return <div className="min-h-screen bg-brand-offwhite flex flex-col items-center justify-center p-6" data-id="element-919">
      <div className="w-12 h-12 bg-brand-charcoal rounded-xl flex items-center justify-center mb-6" data-id="element-920">
        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-921" />
      </div>
      <Skeleton className="h-4 w-48 mb-2" data-id="element-922" />
      <p className="text-sm text-gray-500" data-id="element-923">{message}</p>
    </div>;
}
// ─── Error Screens ───────────────────────────────────────────
function RestaurantNotFound() {
  return <div className="min-h-screen bg-brand-offwhite flex items-center justify-center p-6" data-id="element-924">
      <div className="max-w-md w-full text-center" data-id="element-925">
        <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-926">
          <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} data-id="element-927">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75Z" data-id="element-928" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-929">
          Restaurante no encontrado
        </h1>
        <p className="text-gray-500 mb-8" data-id="element-930">
          No pudimos encontrar un restaurante con esta dirección. Verifica que
          la URL sea correcta.
        </p>
        <a href="/" className="inline-flex items-center justify-center px-6 py-3 bg-brand-charcoal text-white font-medium rounded-xl hover:bg-gray-800 transition-colors" data-id="element-931">
          Volver al inicio
        </a>
      </div>
    </div>;
}
function AccessDenied({
  slug
}: {
  slug: string;
}) {
  return <div className="min-h-screen bg-brand-offwhite flex items-center justify-center p-6" data-id="element-932">
      <div className="max-w-md w-full text-center" data-id="element-933">
        <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-934">
          <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} data-id="element-935">
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636" data-id="element-936" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-937">
          Acceso denegado
        </h1>
        <p className="text-gray-500 mb-8" data-id="element-938">
          No tienes permisos para acceder a este restaurante. Contacta al
          administrador si crees que es un error.
        </p>
        <div className="flex flex-col gap-3" data-id="element-939">
          <a href={`/admin/${slug}/login`} className="inline-flex items-center justify-center px-6 py-3 bg-brand-charcoal text-white font-medium rounded-xl hover:bg-gray-800 transition-colors" data-id="element-940">
            Iniciar sesión con otra cuenta
          </a>
          <a href="/" className="inline-flex items-center justify-center px-6 py-3 text-gray-600 font-medium rounded-xl hover:bg-gray-100 transition-colors" data-id="element-941">
            Volver al inicio
          </a>
        </div>
      </div>
    </div>;
}
// ─── Provider ────────────────────────────────────────────────
interface TenantProviderProps {
  children: React.ReactNode;
  /** Optional slug from subdomain — overrides useParams */
  subdomainSlug?: string;
}
export function TenantProvider({
  children,
  subdomainSlug
}: TenantProviderProps) {
  const {
    slug: pathSlug
  } = useParams<{
    slug: string;
  }>();
  // Priority: explicit subdomainSlug > auto-detected subdomain > path param
  const slug = subdomainSlug || getSubdomainSlug() || pathSlug;
  const {
    user,
    loading: authLoading,
    signOut
  } = useAuth();
  const {
    restaurant,
    loading: restLoading,
    error: restError
  } = useResolvedRestaurant(slug);
  const {
    access,
    loading: accessLoading,
    error: accessError
  } = useTenantAccess(user?.id, restaurant?.restaurant_id);
  // Build the login redirect path based on whether we're on subdomain or path
  const loginPath = subdomainSlug || getSubdomainSlug() ? '/admin/login' : `/admin/${slug}/login`;
  // 1. Loading restaurant
  if (restLoading) {
    return <LoadingScreen message="Cargando restaurante..." data-id="element-942" />;
  }
  // 2. Restaurant not found
  if (restError || !restaurant) {
    return <RestaurantNotFound data-id="element-943" />;
  }
  // 3. Loading auth
  if (authLoading) {
    return <LoadingScreen message="Verificando sesión..." data-id="element-944" />;
  }
  // 4. Not logged in → redirect to tenant login
  if (!user) {
    return <Navigate to={loginPath} replace data-id="element-945" />;
  }
  // 5. Loading access
  if (accessLoading) {
    return <LoadingScreen message="Verificando permisos..." data-id="element-946" />;
  }
  // 6. Access denied
  if (accessError || !access) {
    return <AccessDenied slug={slug || ''} data-id="element-947" />;
  }
  const value: TenantContextValue = {
    restaurant,
    access,
    role: access.role,
    permissions: access.permissions || [],
    user,
    signOut
  };
  return <TenantContext.Provider value={value} data-id="element-948">{children}</TenantContext.Provider>;
}